import {LocalStorage} from './localStorage';
import {CookieStorage} from './cookieStorage';
import {MemoryStorage} from './memoryStorage';
import {debug, warn} from '../utils/log';

const TAG = 'Storage';

export interface StorageOptions {
  expires: number; // in days
}

// note: 'Storage' is already claimed by the Web Storage API
export interface StorageInterface {
  get(key: string): string | undefined;
  set(key: string, value: string, options?: StorageOptions): void;
  remove(key: string): void;
}

let storageInstance: StorageInterface | undefined = undefined;
let partyStorageInstance: StorageInterface | undefined = undefined;

/**
 * Probe whether a storage backend can reliably write and read back a value.
 * Some environments (privacy browsers, blocked cookies, broken set-top boxes)
 * expose a storage API but silently discard writes, so a round-trip check is
 * the only way to confirm it actually works.
 */
function isStorageWritable(storage: StorageInterface): boolean {
  const testKey = 'npo_tag_storage_tester';
  const magicValue = (Math.random() * 20428572).toFixed(0);
  try {
    storage.set(testKey, magicValue, {expires: 1});
    const retrieved = storage.get(testKey);
    storage.remove(testKey);
    return retrieved === magicValue;
  } catch {
    return false;
  }
}

export function createStorage(): StorageInterface {
  if (storageInstance) {
    return storageInstance;
  }

  try {
    // note: just *accessing* localStorage will throw an exception in firefox when local storage is blocked
    if (window && window.localStorage) {
      // test it - it appears some broken set-top boxes *seem* to support local storage, but actually don't
      const candidate = new LocalStorage();
      if (isStorageWritable(candidate)) {
        storageInstance = candidate;
        return storageInstance;
      }
      // We *seemed* to have local storage, but it didn't actually retrieve a value?
      // TODO: send event to topspin containing the platform we're running on
      warn(TAG, 'Not using local storage because probe failed');
    }
  } catch (e) {
    warn(TAG, 'Not using local storage because: ', e);
  }

  // fall back to cookie storage
  debug(TAG, 'Try cookie storage');
  const cookieCandidate = new CookieStorage();
  if (isStorageWritable(cookieCandidate)) {
    storageInstance = cookieCandidate;
    return storageInstance;
  }
  warn(TAG, 'Not using cookie storage because probe failed');

  debug(TAG, 'Falling back to memory storage');
  storageInstance = new MemoryStorage();
  return storageInstance;
}

export const getStorage: () => StorageInterface = () => {
  if (!storageInstance) {
    warn(TAG, 'Storage used before being created?');
    storageInstance = createStorage();
  }
  return storageInstance;
};

function areCookiesAvailable(): boolean {
  return isStorageWritable(new CookieStorage());
}

/**
 * Create (or replace) the storage used for party ID, session ID, brand ID, and
 * last-use timestamp. When cookie_domain is set, a CookieStorage scoped to that
 * domain is used so all values are shared across subdomains — this is the mechanism
 * that enables cross-subdomain session sharing.
 *
 * The cookie storage is probed before use: if the browser blocks or rejects cookies
 * (e.g. privacy mode, user preference), we fall back to the main storage so IDs still
 * persist within the subdomain even though cross-subdomain sharing won't work.
 *
 * Without cookie_domain, falls back to the main storage so behaviour is identical to before.
 */
export function createPartyStorage(cookieDomain?: string): StorageInterface {
  if (cookieDomain) {
    // Probing without a domain is sufficient: if cookies work at all on this page,
    // same-site domain cookies will work too.
    if (areCookiesAvailable()) {
      partyStorageInstance = new CookieStorage(cookieDomain);
      return partyStorageInstance;
    }
    warn(TAG, 'Cookie storage unavailable, falling back to main storage');
  }
  partyStorageInstance = createStorage();
  return partyStorageInstance;
}

export const getPartyStorage: () => StorageInterface = () => {
  if (!partyStorageInstance) {
    partyStorageInstance = createStorage();
  }
  return partyStorageInstance;
};

/** Reset storage singletons. Only intended for use in tests. */
export function _resetStorageForTesting(): void {
  storageInstance = undefined;
  partyStorageInstance = undefined;
}
